import { makeStyles } from '@material-ui/core';
import { Link, useSidebarOpenState } from '@backstage/core-components';
import { useLocation } from 'react-router-dom';
import { isActivePath } from './activePath';
import { NavIcon } from './navIcons';
import { SHELF_ROW, useShelfFilter } from './SidebarShelf';

/**
 * One entry in the navigation column, drawn as the public portals draw theirs
 * (DX-352).
 *
 * Every row in the column is this one. Backstage's own `SidebarItem` is a
 * 48px bar with bold text and a 4px mark down its left edge; the Developer
 * Portal and Help draw a 32px row with a 14px line icon, regular text and a
 * tinted, rounded fill for the current page — the Cockpit's `SidebarNavLink`,
 * which the Developer Portal ported 1:1 into `.docs-sidebar-link`. Restyling
 * Backstage's row into that would take an override of most of its classes,
 * each one an upgrade risk; one row of our own takes none.
 *
 * It began as the row with a count (DX-302): the target picture prints how
 * many things are behind Catalog and Docs, so the entry answers "is it worth
 * opening" before a reader opens it. The count is optional and renders nothing
 * when absent. A number that could not be determined is not zero (see
 * useNavCounts), and a reader who sees no number learns only that the portal
 * did not say — which is true — rather than that the catalog is empty, which
 * would not be.
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

const useStyles = makeStyles({
  /*
    The Developer Portal's `.docs-sidebar-list` puts 0.5rem either side of the
    rows and 2px between them; here the rows are the drawer's direct children,
    so each carries its own share of both.
  */
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    width: 'calc(100% - 1rem)',
    margin: '0 0.5rem 2px',
    padding: '0.375rem 0.5rem',
    boxSizing: 'border-box',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontWeight: 400,
    color: 'var(--ui-nav-link)',
    textDecoration: 'none',
    transition: 'background-color 160ms ease, color 160ms ease',
    '&:hover': {
      background: 'var(--ui-nav-hover-bg)',
      color: 'var(--ui-nav-link-hover)',
      textDecoration: 'none',
    },
    '&:hover $icon': {
      color: 'var(--ui-nav-icon-active)',
    },
  },
  itemActive: {
    background: 'var(--ui-nav-active-bg)',
    color: 'var(--ui-nav-link-active)',
    fontWeight: 500,
    '&:hover': {
      background: 'var(--ui-nav-active-bg)',
      color: 'var(--ui-nav-link-active)',
    },
    '& $icon': {
      color: 'var(--ui-nav-icon-active)',
    },
    '.dark &': {
      fontWeight: 600,
    },
  },
  /*
    Collapsed, the column is Backstage's 72px: the icon alone, centred, in a
    row as wide as the column allows.
  */
  itemClosed: {
    justifyContent: 'center',
    padding: '0.5rem 0',
  },
  icon: {
    display: 'flex',
    flex: '0 0 auto',
    color: 'var(--ui-nav-icon)',
    transition: 'color 160ms ease',
  },
  text: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  /*
    Tabular figures so the counts line up under each other, and dimmed
    against the row's own colour so the number reads as metadata about the
    entry rather than as part of its name. `currentColor` at reduced opacity
    rather than a second colour value: it then follows the row through hover
    and selection without a rule for each state.
  */
  count: {
    flexShrink: 0,
    fontSize: '0.75rem',
    fontVariantNumeric: 'tabular-nums',
    color: 'currentColor',
    opacity: 0.6,
  },
  /*
    Collapsed, the text would show as its first letter beside the icon, so
    it is hidden from sight — and only from sight: the name is still what a
    screen reader announces.
  */
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
});

type SidebarNavItemProps = {
  icon: NavIcon;
  text: string;
  to: string;
  /** Omitted when the count could not be determined — then nothing is shown. */
  count?: number;
  /**
   * Marked on this path alone rather than on everything under it. Home needs
   * it: `/` is a prefix of every path in the portal.
   */
  end?: boolean;
};

export const SidebarNavItem = ({
  icon: Icon,
  text,
  to,
  count,
  end,
}: SidebarNavItemProps) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const { isOpen } = useSidebarOpenState();
  const { matches } = useShelfFilter();

  if (!matches(text)) {
    return null;
  }

  // The top bar's rule: a reader three levels into an entity has not left
  // the catalog, and the entry that brought them there stays marked.
  const active = isActivePath(pathname, to, end);

  return (
    <Link
      to={to}
      {...SHELF_ROW}
      className={[
        classes.item,
        active ? classes.itemActive : '',
        isOpen ? '' : classes.itemClosed,
      ].join(' ')}
      aria-current={active ? 'page' : undefined}
    >
      <span className={classes.icon}>
        <Icon />
      </span>
      <span className={isOpen ? classes.text : classes.visuallyHidden}>
        {text}
      </span>
      {count !== undefined && isOpen && (
        /*
          Spelled out for a screen reader: on its own the number reads as
          "Catalog 118", which sounds like a name rather than a quantity.
          Grouped in thousands the way the page it leads to writes it: the
          docs hub's search field states "9,130 pages", and the entry beside
          it read "9130" (DX-306).
        */
        <span
          className={classes.count}
          aria-label={`${count.toLocaleString('en-US')} entries`}
        >
          {count.toLocaleString('en-US')}
        </span>
      )}
    </Link>
  );
};
